import { useEffect, useMemo, useRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { Button, Radio, RadioGroupProps, Form } from 'antd';
import { DisabledProps } from 'views/Aide/Scenario/TaskForm/share';
import FormField from 'components/FormField';
import useSmartField from 'hooks/useSmartField';
import { VoucherSelector } from '@knowledge/Activity/ActivityForm/VoucherConfig/VoucherSelector';
import { useMemoizedFn, useToggle } from 'ahooks';
import { VoucherDisplay } from '@knowledge/Activity/ActivityForm/VoucherConfig/VoucherDisplay';
import { isTiKTok } from 'lib/utils/isTikTok';
import { FV } from '@knowledge/Activity/ActivityForm/share';

interface VoucherConfigProps extends DisabledProps {
  isEdit?: boolean;
}

export function VoucherConfig(props: VoucherConfigProps) {
  const { disabled, isEdit } = props;
  const { t } = useTranslate();
  const [modalVisible, { toggle: toggleModalVisible }] = useToggle(false);

  const {
    field: { value: storeList },
  } = useSmartField<Knowledge.Activity.Store[]>('stores');
  const { field: activityVoucherTypeField } =
    useSmartField<FV['activityVoucherType']>('activityVoucherType');
  const { field: voucherListField, helpers: voucherListHelpers } =
    useSmartField<FV['voucherList']>('voucherList');

  const initRef = useRef(false);

  // 是否存在可发送优惠券的平台 Shopee/Lazada
  const existCanSendPlatform = useMemo(() => {
    return storeList?.some(
      (v) => v.platform === 'SHOPEE' || v.platform === 'LAZADA' || isTiKTok(v.platform),
    );
  }, [storeList]);

  const radioOptions: RadioGroupProps['options'] = useMemo(() => {
    return [
      {
        label: t('send_voucher'),
        value: 'SEND_VOUCHER_CARD',
        disabled: !existCanSendPlatform,
      },
      {
        label: t('do_not_send_voucher'),
        value: 'NOT_SEND_VOUCHER_CARD',
      },
    ];
  }, [t, existCanSendPlatform]);

  const showVoucherSelector = useMemo(
    () => activityVoucherTypeField.value === 'SEND_VOUCHER_CARD' && storeList?.length > 0,
    [activityVoucherTypeField.value, storeList],
  );

  const updateVoucherListByPlatform = useMemoizedFn(() => {
    activityVoucherTypeField.onChange('SEND_VOUCHER_CARD');
    requestAnimationFrame(() => {
      voucherListHelpers.setValue(
        voucherListField.value?.filter(
          (voucher) => !!storeList.find((store) => store.storeId === voucher.storeId),
        ),
      );
    });
  });

  const cleanVoucherList = useMemoizedFn(() => {
    activityVoucherTypeField.onChange('NOT_SEND_VOUCHER_CARD');
    voucherListHelpers.setValue([]);
  });

  useEffect(() => {
    if (disabled) return;
    /**
     * 编辑时初始化 activityVoucherType
     * 后续根据 store 重新刷新 activityVoucherType
     */
    if (isEdit && !initRef.current) {
      initRef.current = true;
      return;
    }
    /**
     * 初始化
     * Shopee、Lazada、TikTok 平台默认选中[发送优惠券]
     */
    const checkPlatform = storeList.every(
      (v) => v.platform === 'SHOPEE' || v.platform === 'LAZADA' || isTiKTok(v.platform),
    );
    if (storeList.length && checkPlatform) {
      updateVoucherListByPlatform();
    } else {
      cleanVoucherList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeList]);

  return (
    <Form.Item label={t('tab.voucher')}>
      <FormField<FV['activityVoucherType']> name="activityVoucherType" noStyle>
        {({ field: { value, onChange } }) => (
          <Radio.Group
            className="!mt-1.5"
            defaultValue="NOT_SEND_VOUCHER_CARD"
            options={radioOptions}
            disabled={disabled}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              if (e.target.value === 'NOT_SEND_VOUCHER_CARD') {
                voucherListField.onChange([]);
              }
            }}
          />
        )}
      </FormField>
      <FormField<FV['voucherList']>
        name="voucherList"
        validateTrigger={['onChange']}
        noStyle
        validate={(value) => {
          if (
            activityVoucherTypeField.value === 'SEND_VOUCHER_CARD' &&
            (storeList.length !== value?.length ||
              !storeList.every((store) => !!value?.find((v) => v.storeId === store.storeId)))
          ) {
            return t('activity_form_voucher_list_rule1');
          }
          return undefined;
        }}
      >
        {({ field: { value, onChange }, meta }) => {
          const voucherItem = value?.[0];
          return (
            showVoucherSelector && (
              <>
                <div className="flex items-end">
                  {voucherItem && <VoucherDisplay data={voucherItem} />}
                  {!disabled && (
                    <Button type="link" className="!px-0" onClick={toggleModalVisible}>
                      {voucherItem ? t('phrase.重新选择') : `+${t('select_voucher')}`}
                    </Button>
                  )}
                </div>
                <div className="mt-1 ant-form-item-explain-error">{meta.error}</div>
                <VoucherSelector
                  value={value}
                  visible={modalVisible}
                  storeList={storeList}
                  onCancel={toggleModalVisible}
                  onChange={onChange}
                />
              </>
            )
          );
        }}
      </FormField>
    </Form.Item>
  );
}
